import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Eye, 
  EyeOff, 
  Search, 
  User, 
  Building2, 
  Shield, 
  CheckCircle, 
  XCircle,
  Loader2,
  Users,
  AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  fetchAllUsersForDebugger, 
  fetchUserAgentVisibility, 
  UserProfile, 
  UserVisibilityReport 
} from '@/lib/api/debugger';

export default function UserViewDebugger() {
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState<'all' | 'visible' | 'hidden'>('all');

  // Fetch all users for dropdown
  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ['debugger-users'],
    queryFn: fetchAllUsersForDebugger,
  });

  // Fetch visibility report for selected user
  const { data: visibilityReport, isLoading: reportLoading, error: reportError } = useQuery({
    queryKey: ['user-visibility', selectedUserId],
    queryFn: () => fetchUserAgentVisibility(selectedUserId),
    enabled: !!selectedUserId,
  });

  // Filter users for search
  const filteredUsers = users.filter(user => 
    user.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter agents based on visibility filter
  const filteredAgents = visibilityReport?.agents.filter(agent => {
    if (visibilityFilter === 'visible') return agent.isVisible;
    if (visibilityFilter === 'hidden') return !agent.isVisible;
    return true;
  }) || [];

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'admin': return 'destructive';
      case 'company_admin': return 'default';
      case 'editor': return 'secondary';
      default: return 'outline';
    }
  };

  const formatAgentType = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const formatPlatform = (platform: string) => {
    if (platform === 'elevenlabs') return 'Audio';
    if (platform === 'anam') return 'Video';
    return platform;
  };

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <Eye className="h-8 w-8 text-primary" />
            User View Debugger
          </h1>
          <p className="text-muted-foreground mt-1">
            Debug RLS policies by viewing what each user sees
          </p>
        </div>
      </div>

      {/* User Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Select User
          </CardTitle>
          <CardDescription>
            Choose a user to see what agents they can access
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search users by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="mb-2"
              />
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a user..." />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {usersLoading ? (
                    <div className="p-4 text-center">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                    </div>
                  ) : (
                    filteredUsers.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{user.display_name}</span>
                          <span className="text-muted-foreground">({user.email})</span>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User Details */}
      {selectedUserId && visibilityReport && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              User Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                <User className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="text-sm text-muted-foreground">Name</div>
                  <div className="font-medium">{visibilityReport.user.display_name}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                <Building2 className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="text-sm text-muted-foreground">Company</div>
                  <div className="font-medium">
                    {visibilityReport.user.company_name || 'No company'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                <Shield className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="text-sm text-muted-foreground">Roles</div>
                  <div className="flex gap-1 flex-wrap mt-1">
                    {visibilityReport.roles.map(role => (
                      <Badge key={role} variant={getRoleBadgeVariant(role)}>
                        {role}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading/Error States */}
      {selectedUserId && reportLoading && (
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="mt-2 text-muted-foreground">Loading visibility report...</p>
          </CardContent>
        </Card>
      )}

      {selectedUserId && reportError && (
        <Card className="border-destructive">
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-8 w-8 mx-auto text-destructive" />
            <p className="mt-2 text-destructive">Error loading visibility report</p>
          </CardContent>
        </Card>
      )}

      {/* Agent Visibility Report */}
      {visibilityReport && !reportLoading && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Agent Visibility
                </CardTitle>
                <CardDescription className="mt-1">
                  <span className="text-success font-medium">{visibilityReport.visibleCount} visible</span>
                  <span className="mx-2">•</span>
                  <span className="text-destructive font-medium">{visibilityReport.hiddenCount} hidden</span>
                </CardDescription>
              </div>
              <Tabs value={visibilityFilter} onValueChange={(v) => setVisibilityFilter(v as any)}>
                <TabsList>
                  <TabsTrigger value="all">
                    All ({visibilityReport.agents.length})
                  </TabsTrigger>
                  <TabsTrigger value="visible">
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Visible ({visibilityReport.visibleCount})
                  </TabsTrigger>
                  <TabsTrigger value="hidden">
                    <XCircle className="h-4 w-4 mr-1" />
                    Hidden ({visibilityReport.hiddenCount})
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">Status</TableHead>
                    <TableHead>Agent Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Platform</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Assign Mode</TableHead>
                    <TableHead>Visibility Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAgents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No agents match the current filter
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAgents.map(agent => (
                      <TableRow 
                        key={agent.id} 
                        className={agent.isVisible ? '' : 'bg-muted/30'}
                      >
                        <TableCell>
                          {agent.isVisible ? (
                            <CheckCircle className="h-5 w-5 text-success" />
                          ) : (
                            <XCircle className="h-5 w-5 text-destructive" />
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{agent.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{formatAgentType(agent.type)}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{formatPlatform(agent.platform)}</Badge>
                        </TableCell>
                        <TableCell>
                          {agent.company_name || (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={agent.assign_to === 'all' ? 'default' : 'outline'}>
                            {agent.assign_to || 'all'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {agent.visibilityReason}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No User Selected */}
      {!selectedUserId && (
        <Card>
          <CardContent className="py-16 text-center">
            <Eye className="h-12 w-12 mx-auto text-muted-foreground/50" />
            <p className="mt-4 text-lg text-muted-foreground">
              Select a user above to see their agent visibility
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              This tool helps debug RLS policies and ensure users see the correct agents
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
